import { supabase } from '../config/supabase';
import { UserWithClubs } from '../types';

export const adminService = {
  /**
   * Get all users with their club memberships
   */
  async getAllUsers(): Promise<UserWithClubs[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select(`
        *,
        club_memberships:user_clubs(
          user_id,
          club_id,
          role,
          joined_at,
          club:clubs(id, name)
        )
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  /**
   * Promote user to platform admin
   */
  async promoteToAdmin(userId: string): Promise<void> {
    const { error } = await supabase
      .from('profiles')
      .update({ is_platform_admin: true })
      .eq('id', userId);

    if (error) throw error;
  },

  /**
   * Demote user from platform admin
   */
  async demoteFromAdmin(userId: string): Promise<void> {
    const { error } = await supabase
      .from('profiles')
      .update({ is_platform_admin: false })
      .eq('id', userId);

    if (error) throw error;
  },

  /**
   * Promote user to club manager
   */
  async promoteToClubManager(userId: string, clubId: string): Promise<void> {
    const { error } = await supabase
      .from('user_clubs')
      .upsert({
        user_id: userId,
        club_id: clubId,
        role: 'manager',
      });

    if (error) throw error;
  },

  /**
   * Demote user from club manager (remove from club)
   */
  async demoteFromClubManager(userId: string, clubId: string): Promise<void> {
    const { error } = await supabase
      .from('user_clubs')
      .delete()
      .eq('user_id', userId)
      .eq('club_id', clubId);

    if (error) throw error;
  },

  /**
   * Check if current user is platform admin
   */
  async isCurrentUserAdmin(): Promise<boolean> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase
      .from('profiles')
      .select('is_platform_admin')
      .eq('id', user.id)
      .single();

    if (error) return false;
    return data?.is_platform_admin || false;
  },

  /**
   * Check if user is a manager of a specific club
   * @param userId User ID to check
   * @param clubId Club ID to check
   * @returns True if user is manager, false otherwise
   */
  async isUserClubManager(userId: string, clubId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('user_clubs')
      .select('role')
      .eq('user_id', userId)
      .eq('club_id', clubId)
      .maybeSingle();

    if (error) {
      console.error('Error checking club manager status:', error);
      return false;
    }

    return data?.role === 'manager';
  },

  /**
   * Get admin setting by key
   */
  async getAdminSetting(key: string): Promise<any> {
    const { data, error } = await supabase
      .from('admin_settings')
      .select('setting_value')
      .eq('setting_key', key)
      .single();

    if (error) throw error;
    return data?.setting_value;
  },

  /**
   * Update admin setting by key
   */
  async updateAdminSetting(key: string, value: any): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('admin_settings')
      .upsert({
        setting_key: key,
        setting_value: value,
        updated_by: user.id,
        updated_at: new Date().toISOString(),
      });

    if (error) throw error;
  },

  /**
   * Get platform transaction fee
   * @returns Platform fee in EUR (defaults to 1.0 if not set)
   */
  async getPlatformTransactionFee(): Promise<number> {
    try {
      const feeValue = await this.getAdminSetting('platform_transaction_fee');
      return parseFloat(feeValue) || 1.0;
    } catch (error) {
      // Return default if setting doesn't exist
      return 1.0;
    }
  },

  /**
   * Update platform transaction fee (admin only - enforced by RLS)
   * @param amount Fee amount in EUR
   */
  async softDeleteUser(userId: string, reason: string): Promise<void> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
    const response = await fetch(`${supabaseUrl}/functions/v1/admin-delete-account`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user_id: userId, reason }),
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.error ?? 'Account deletion failed');
    }
  },

  async updatePlatformTransactionFee(amount: number): Promise<void> {
    if (amount < 0) {
      throw new Error('Platform fee cannot be negative');
    }

    if (amount > 10) {
      throw new Error('Platform fee cannot exceed €10');
    }

    await this.updateAdminSetting('platform_transaction_fee', amount.toString());
  },
};
